// VyaktitvaDarshanTab.tsx
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UserCircle } from "lucide-react";
import AstriaIndiaV2Shell, { SHELL_COLORS_INDIA_V2 } from "../AstriaIndiaV2Shell";
import {
  AstriaIndiaV2ResultCard,
  AstriaIndiaV2FieldBlock,
  AstriaIndiaV2HighlightBadge,
  AstriaIndiaV2SignatureLine,
  IndiaV2LoadingState,
  IndiaV2ErrorState,
  IndiaV2ChatCta,
} from "../AstriaIndiaV2Card";
import {
  WizardProgress,
  WizardStep,
  WizardNav,
  WizardChips,
  WizardDropdown,
} from "../AstriaIndiaV2Wizard";
import { useAstriaIndiaV2Tab } from "../useAstriaIndiaV2Tab";
import { AstriaIndiaV2VyaktitvaData } from "../../../../contexts/ChatContext";
import { useIndiaV2Language } from "../../../../utils/India/astriaIndiaV2Language";
import { ASTRIA_INDIA_V2_COPY } from "../../../../utils/India/astriaIndiaV2Copy";

const SUBCATEGORY_NAME = "Vyaktitva Darshan";

const TOTAL_STEPS = 3;

const VyaktitvaDarshanTab: React.FC = () => {
  // Which engine to call is decided ONLY by which sidebar entry the user
  // clicked ("Astria India V2" vs "Astria India V3" — see
  // Sidebar.tsx/TesterHome.tsx's "?v=3" query flag) and never changes for
  // the lifetime of this page — no in-page switch.
  const [searchParams] = useSearchParams();
  const version: "v2" | "v3" = searchParams.get("v") === "3" ? "v3" : "v2";
  const categoryName = version === "v3" ? "Astria India V3" : "Astria India V2";

  const [lang, setLang] = useIndiaV2Language();
  const copy = ASTRIA_INDIA_V2_COPY[lang];
  const t = copy.vyaktitva;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } =
    useAstriaIndiaV2Tab({
      categoryName,
      subCategoryName: SUBCATEGORY_NAME,
    });

  const [step, setStep] = useState(1);
  const [yourTrait, setYourTrait] = useState<string | null>(null);
  const [theirTrait, setTheirTrait] = useState<string | null>(null);
  const [style, setStyle] = useState<string | null>(null);

  const started = !!message || loading || !!error;

  const buildMessage = () => {
    const yourLabel =
      t.yourTraits.find((tr) => tr.value === yourTrait)?.label || "";
    const theirLabel =
      t.theirTraits.find((tr) => tr.value === theirTrait)?.label || "";
    const styleLabel =
      t.styleOptions.find((s) => s.value === style)?.label || "";

    return `Please give me a Vyaktitva Darshan personality reading.
I'd describe myself as: ${yourLabel || "still discovering myself"}.
The other person is: ${theirLabel || "someone close to me"}.
Our interaction style is usually: ${styleLabel || "a mix of everything"}.`;
  };

  const handleSubmit = () => {
    send(buildMessage());
  };

  const vyaktitvaData = message?.astriaIndiaV2Data as
    | AstriaIndiaV2VyaktitvaData
    | undefined;

  if (started) {
    return (
      <AstriaIndiaV2Shell
        active="vyaktitva"
        lang={lang}
        onLangChange={setLang}
        version={version}
      >
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#2E1F45",
              marginBottom: 4,
            }}
          >
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: "#8C7DA0" }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && (
          <IndiaV2LoadingState label={t.loading} />
        )}

        {!loading && error && (
          <IndiaV2ErrorState message={error} onRetry={handleSubmit} retryLabel={shared.tryAgain} />
        )}

        {!loading && !error && vyaktitvaData && (
          <>
            <AstriaIndiaV2ResultCard
              icon={<UserCircle className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              accent={["#4F6FD9", "#6B3FA0"]}
              shareText={`${t.tabLabel}\n\n${vyaktitvaData.core_nature}\n\n${vyaktitvaData.growth_invitation}`}
              shareLabels={shared}
            >
              <AstriaIndiaV2HighlightBadge text={vyaktitvaData.highlight} />
              <AstriaIndiaV2FieldBlock
                label={t.fields.coreNature}
                value={vyaktitvaData.core_nature}
              />
              <AstriaIndiaV2SignatureLine value={vyaktitvaData.micro_imagery} />
              <AstriaIndiaV2SignatureLine value={vyaktitvaData.astro_soft_influence} />
              <AstriaIndiaV2FieldBlock
                label={t.fields.emotionalPattern}
                value={vyaktitvaData.emotional_pattern}
              />
              <AstriaIndiaV2FieldBlock
                label={t.fields.innerRhythm}
                value={vyaktitvaData.inner_rhythm}
              />
              <AstriaIndiaV2FieldBlock
                label={t.fields.relationshipStyle}
                value={vyaktitvaData.relationship_style}
              />
              <AstriaIndiaV2FieldBlock
                label={t.fields.growthInvitation}
                value={vyaktitvaData.growth_invitation}
              />

              {vyaktitvaData.actions?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: SHELL_COLORS_INDIA_V2.accent,
                      marginBottom: 8,
                    }}
                  >
                    {t.fields.actions}
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {vyaktitvaData.actions.map((a, i) => (
                      <li
                        key={i}
                        style={{ fontSize: 14.5, lineHeight: 1.7, color: "#4A3B5C", marginBottom: 4 }}
                      >
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </AstriaIndiaV2ResultCard>
            <button
              onClick={() => {
                reset();
                setStep(1);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: "#8C7DA0" }}
            >
              {shared.startNewReading}
            </button>
            <IndiaV2ChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !vyaktitvaData && message && (
          <AstriaIndiaV2ResultCard
            icon={<UserCircle className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
            accent={["#4F6FD9", "#6B3FA0"]}
          >
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.8,
                color: "#4A3B5C",
                whiteSpace: "pre-wrap",
              }}
            >
              {message.content}
            </p>
          </AstriaIndiaV2ResultCard>
        )}
      </AstriaIndiaV2Shell>
    );
  }

  return (
    <AstriaIndiaV2Shell
      active="vyaktitva"
      lang={lang}
      onLangChange={setLang}
      version={version}
    >
      <WizardProgress
        step={step}
        total={TOTAL_STEPS}
        accent="#4F6FD9"
        stepLabel={shared.stepOf}
        percentLabel={shared.percentComplete}
      />

      {step === 1 && (
        <WizardStep stepKey="you" question={t.step1Question}>
          <WizardChips
            options={t.yourTraits}
            value={yourTrait}
            onChange={setYourTrait}
          />
          <WizardNav
            showBack={false}
            nextDisabled={!yourTrait}
            onNext={() => setStep(2)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 2 && (
        <WizardStep stepKey="them" question={t.step2Question}>
          <WizardChips
            options={t.theirTraits}
            value={theirTrait}
            onChange={setTheirTrait}
          />
          <WizardNav
            onBack={() => setStep(1)}
            nextDisabled={!theirTrait}
            onNext={() => setStep(3)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 3 && (
        <WizardStep
          stepKey="style"
          question={t.step3Question}
        >
          <WizardDropdown
            options={t.styleOptions}
            value={style}
            onChange={setStyle}
            placeholder={t.stylePlaceholder}
          />
          <WizardNav
            onBack={() => setStep(2)}
            nextLabel={t.revealButton}
            nextDisabled={!style}
            onNext={handleSubmit}
            backLabel={shared.back}
          />
        </WizardStep>
      )}
    </AstriaIndiaV2Shell>
  );
};

export default VyaktitvaDarshanTab;
